/**
 * MUTAGIM.SHOP — Flash Sale Row (Phase 15)
 * Ligne Netflix spéciale pour les ventes flash avec compte à rebours
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Zap, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function FlashSaleRow({ className }) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const scrollRef = useRef(null);
  const [flashSales, setFlashSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState({});
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const isRTL = i18n.dir() === 'rtl';
  const lang = i18n.language;

  // Charger les flash sales
  useEffect(() => {
    const fetchFlashSales = async () => {
      try {
        const res = await fetch(`${API_URL}/api/promotions/flash-sales?active_only=true`);
        if (res.ok) {
          const data = await res.json();
          setFlashSales(data.flash_sales || []);
          
          // Initialiser les timers
          const times = {};
          data.flash_sales?.forEach(fs => {
            times[fs.id] = fs.time_remaining || 0;
          });
          setTimeRemaining(times);
        }
      } catch (err) {
        console.error('Error fetching flash sales:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFlashSales();
  }, []);

  // Compte à rebours
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(id => {
          if (updated[id] > 0) {
            updated[id] -= 1;
          }
        });
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Formater le temps restant
  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return { hours: '00', minutes: '00', secs: '00' };
    const totalSeconds = Math.floor(seconds);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return {
      hours: h.toString().padStart(2, '0'),
      minutes: m.toString().padStart(2, '0'),
      secs: s.toString().padStart(2, '0'),
    };
  };

  // Navigation scroll
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const effectiveScroll = isRTL ? -scrollLeft : scrollLeft;
      setCanScrollLeft(effectiveScroll > 10);
      setCanScrollRight(effectiveScroll < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      return () => container.removeEventListener('scroll', checkScroll);
    }
  }, [flashSales]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      const currentScroll = scrollRef.current.scrollLeft;
      const actualDirection = isRTL ? (direction === 'left' ? 'right' : 'left') : direction;
      const newScroll = actualDirection === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount;
      scrollRef.current.scrollTo({ left: newScroll, behavior: 'smooth' });
    }
  };

  // Ne pas afficher si pas de flash sales actives
  if (loading || flashSales.length === 0) return null;

  // Collecter tous les produits de toutes les flash sales
  const allProducts = flashSales.flatMap(fs => 
    (fs.products || []).map(p => ({
      ...p,
      discount: fs.discount_percent,
      endDate: fs.end_date,
      flashSaleId: fs.id,
      timeLeft: timeRemaining[fs.id] || 0,
    }))
  );

  if (allProducts.length === 0) return null;

  return (
    <section 
      className={cn("relative group/row", className)}
      data-testid="flash-sale-row"
    >
      {/* Header avec badge animé */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-orange-500 px-4 py-2 rounded-lg animate-pulse">
            <Zap className="w-5 h-5 text-white fill-white" />
            <span className="text-white font-bold text-lg">
              {t('promotions.flashSale', 'FLASH SALE')}
            </span>
          </div>
          
          {/* Timer global (première flash sale) */}
          {flashSales[0] && (
            <div className="hidden md:flex items-center gap-2 bg-black/60 px-3 py-2 rounded-lg border border-red-500/50">
              <Clock className="w-4 h-4 text-red-400" />
              <div className="flex items-center gap-1 font-mono text-white">
                <span className="bg-red-600/30 px-2 py-1 rounded">
                  {formatTime(timeRemaining[flashSales[0].id]).hours}
                </span>
                <span className="text-red-400">:</span>
                <span className="bg-red-600/30 px-2 py-1 rounded">
                  {formatTime(timeRemaining[flashSales[0].id]).minutes}
                </span>
                <span className="text-red-400">:</span>
                <span className="bg-red-600/30 px-2 py-1 rounded">
                  {formatTime(timeRemaining[flashSales[0].id]).secs}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="hidden md:flex items-center gap-2 opacity-0 group-hover/row:opacity-100 transition-opacity">
          <Button
            variant="outline"
            size="icon"
            className={cn("border-gray-700 text-white hover:bg-red-600 hover:border-red-600", !canScrollLeft && "opacity-30")}
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
          >
            {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className={cn("border-gray-700 text-white hover:bg-red-600 hover:border-red-600", !canScrollRight && "opacity-30")}
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
          >
            {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Products row */}
      <div className="relative">
        {canScrollLeft && (
          <div className={cn(
            "absolute top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-r from-mutagim-dark to-transparent",
            isRTL ? "right-0" : "left-0"
          )} />
        )}
        {canScrollRight && (
          <div className={cn(
            "absolute top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-l from-mutagim-dark to-transparent",
            isRTL ? "left-0" : "right-0"
          )} />
        )}

        <div
          ref={scrollRef}
          className={cn(
            "flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4",
            isRTL && "flex-row-reverse"
          )}
        >
          {allProducts.map((product, idx) => {
            const time = formatTime(product.timeLeft);
            return (
              <Link
                key={`${product.id}-${idx}`}
                to={`/product/${product.slug}`}
                className="flex-shrink-0 w-[200px] md:w-[240px] group"
              >
                <div className="relative bg-gray-900 rounded-lg overflow-hidden border-2 border-red-500/50 hover:border-red-500 transition-all hover:shadow-lg hover:shadow-red-500/20">
                  {/* Image */}
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={product.image || product.images?.[0] || '/placeholder-product.svg'}
                      alt={product.name?.[lang] || product.name?.fr || ''}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder-product.svg';
                      }}
                    />
                    
                    {/* Flash Sale Badge */}
                    <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
                      <Badge className="bg-red-600 text-white font-bold animate-pulse">
                        <Zap className="w-3 h-3 mr-1 fill-white" />
                        -{product.discount}%
                      </Badge>
                      
                      {/* Mini timer */}
                      <div className="bg-black/80 text-white text-xs px-2 py-1 rounded font-mono">
                        {time.hours}:{time.minutes}:{time.secs}
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-3">
                    <h3 className="text-white text-sm font-medium line-clamp-2 mb-2 min-h-[2.5rem]">
                      {product.name?.[lang] || product.name?.fr || 'Produit'}
                    </h3>
                    
                    <div className="flex items-baseline gap-2">
                      <span className="text-red-500 font-bold text-lg">
                        {formatPrice({ EUR: product.flash_price })}
                      </span>
                      <span className="text-gray-500 line-through text-sm">
                        {formatPrice({ EUR: product.original_price })}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default FlashSaleRow;
